// Requests for Tulevaisuustestaus v2 (#1474), copied from v1's futureFetch.js. Paths come from API_ENDPOINTS (config/api.js).
import { useCallback, useEffect, useState } from 'react';
import API_ENDPOINTS from '../../config/api';
import useContentRefresh from '../../hooks/useContentRefresh';
import { getOrCreateParticipantId } from '../../utils/participantId';
import socketService from '../../../../../packages/shared/socket';

/**
 * JSON request with the session cookie and this device's participant id (the keep-vote counts
 * per device); throws the server's error message and code.
 */
export async function request(url, { method = 'GET', body } = {}) {
  const participantId = getOrCreateParticipantId();
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(participantId ? { 'X-Participant-Id': participantId } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // The module's routes send { error: { code, message } }; the role check sends { error: string }.
    const err = new Error(data.error?.message || (typeof data.error === 'string' ? data.error : `HTTP ${res.status}`));
    err.code = data.error?.code;
    throw err;
  }
  return data;
}

// A backstop for what emits no event: the facilitator's voting switch is an instance setting,
// saved by the shared module controls.
const POLL_MS = 15000;

/**
 * The module's whole state, reloaded when any device changes it (resource 'future_v2') or
 * Ratkaisut v4 changes the solutions it reads ('solutions', 'solutions_v4').
 */
export function useFutureV2State(instanceId) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    if (!instanceId) return;
    try {
      setState(await request(API_ENDPOINTS.FUTURE_V2_STATE(instanceId)));
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [instanceId]);
  // Joins the instance's room before useContentRefresh subscribes: that hook listens only on a
  // socket that already exists (v1's slice-4 browser check).
  useEffect(() => {
    if (instanceId) socketService.connect(instanceId, 'display', `future-v2-${getOrCreateParticipantId() || Date.now()}`);
  }, [instanceId]);
  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);
  useContentRefresh(instanceId, ['future_v2', 'solutions', 'solutions_v4'], load, { minIntervalMs: 1500, enabled: Boolean(instanceId) });
  return { state, error, reload: load };
}
